import { AlertTriangle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import design from '../../design.json';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../components/ui/toast';
import { ApiError } from '../lib/api/client';
import {
  type SignInResponse,
  emailForgot,
  emailLogin,
  emailResendCode,
  emailReset,
  emailVerify,
  signInWithGoogle,
} from '../lib/auth/api';
import { renderGoogleButton } from '../lib/auth/google';
import { useAuthStore } from '../lib/auth/store';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '';

/**
 * No sign-up on the staff console: accounts are created in the apps
 * (correspondents sign up in VON iPlayer) and an admin grants the role. The
 * verify step is only for a staffer whose email isn't verified yet — the
 * login answers 403 and we send a fresh code.
 */
type EmailMode = 'login' | 'verify' | 'forgot' | 'reset';

export default function SignIn(): JSX.Element {
  const token = useAuthStore((s) => s.token);
  const setSession = useAuthStore((s) => s.setSession);
  const location = useLocation();
  const googleHost = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'google' | 'email' | null>(null);
  const [emailMode, setEmailMode] = useState<EmailMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [emailNotice, setEmailNotice] = useState<string | null>(null);
  const { showToast } = useToast();

  useEffect(() => {
    if (!googleHost.current || !GOOGLE_CLIENT_ID) return;
    void renderGoogleButton(googleHost.current, GOOGLE_CLIENT_ID, async (idToken) => {
      setBusy('google');
      setError(null);
      try {
        const resp = await signInWithGoogle(idToken);
        setSession(resp.token, resp.user);
        showToast({ title: `Welcome, ${resp.user.name ?? resp.user.email}`, variant: 'success' });
      } catch (err) {
        setError(formatError(err));
      } finally {
        setBusy(null);
      }
    }).catch((err) => setError(`Google sign-in unavailable: ${(err as Error).message}`));
  }, [setSession, showToast]);

  if (token) {
    const redirect = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={redirect} replace />;
  }

  function welcome(resp: SignInResponse): void {
    setSession(resp.token, resp.user);
    showToast({ title: `Welcome, ${resp.user.name ?? resp.user.email}`, variant: 'success' });
  }

  /** Change email step with a clean slate — no stale error or notice. */
  function switchMode(mode: EmailMode, notice: string | null = null): void {
    setEmailMode(mode);
    setError(null);
    setEmailNotice(notice);
    setCode('');
  }

  async function handleEmailLogin(): Promise<void> {
    setBusy('email');
    setError(null);
    setEmailNotice(null);
    try {
      const resp = await emailLogin(email.trim(), password);
      welcome(resp);
    } catch (err) {
      // The only 403 this endpoint returns is "email not verified".
      if (err instanceof ApiError && err.status === 403) {
        switchMode('verify', 'Your email isn’t verified yet — enter the code we just emailed you.');
        try {
          await emailResendCode(email.trim());
        } catch {
          // Resend is best-effort; the signup code may still be live.
        }
      } else if (err instanceof ApiError && err.status === 401) {
        // On this endpoint 401 means wrong credentials, not a rejected
        // provider token — formatError's generic 401 wording doesn't fit.
        setError('Incorrect email or password.');
      } else {
        setError(formatError(err));
      }
    } finally {
      setBusy(null);
    }
  }

  async function handleEmailVerify(): Promise<void> {
    setBusy('email');
    setError(null);
    setEmailNotice(null);
    try {
      const resp = await emailVerify(email.trim(), code.trim(), password);
      welcome(resp);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setBusy(null);
    }
  }

  async function handleEmailForgot(): Promise<void> {
    setBusy('email');
    setError(null);
    setEmailNotice(null);
    try {
      await emailForgot(email.trim());
      switchMode('reset', 'Code sent — check your inbox.');
    } catch (err) {
      setError(formatError(err));
    } finally {
      setBusy(null);
    }
  }

  async function handleEmailReset(): Promise<void> {
    setBusy('email');
    setError(null);
    setEmailNotice(null);
    try {
      await emailReset(email.trim(), code.trim(), newPassword);
      setPassword('');
      setNewPassword('');
      switchMode('login', 'Password reset — sign in with your new password.');
    } catch (err) {
      setError(formatError(err));
    } finally {
      setBusy(null);
    }
  }

  const today = new Date()
    .toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    .toUpperCase();

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-[1.05fr_1fr]">
      {/* ─── Brand panel ──────────────────────────────────────── */}
      <aside className="relative hidden overflow-hidden bg-brand-radial text-white lg:flex lg:flex-col">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.08] bg-hairline-grid"
          style={{ backgroundSize: 'var(--grid-size) var(--grid-size)' }}
        />
        <div aria-hidden className="absolute inset-y-0 left-0 w-[3px] tricolor-spine" />
        <div className="relative flex h-full flex-col justify-between p-10 xl:p-14">
          <div className="flex items-center gap-3">
            <Logo size={40} withWordmark onDark />
          </div>

          <div className="max-w-lg">
            <p className="eyebrow-on-brand">Newsroom Console · Edition {today}</p>
            <h2 className="mt-4 font-heading text-[40px] font-semibold leading-[1.05] tracking-tight xl:text-[52px]">
              The voice of a nation
              <span className="block text-white/55">runs on this desk.</span>
            </h2>
            <p className="mt-5 max-w-md text-[14px] leading-relaxed text-white/65">
              Manage news, programmes, schedules and audiences across all VON
              channels. Sign in to take the desk.
            </p>
          </div>

          <div className="flex flex-wrap items-end justify-between gap-4 border-t border-white/10 pt-6 font-mono text-[10.5px] uppercase tracking-[0.16em] text-white/55">
            <span>{design.organisation.tagline}</span>
            <span>{design.organisation.website?.replace(/^https?:\/\//, '')}</span>
          </div>
        </div>
      </aside>

      {/* ─── Auth panel ───────────────────────────────────────── */}
      <main className="relative flex items-center justify-center bg-surface px-5 py-12 sm:px-10">
        {/* mobile-only header */}
        <div className="absolute left-5 top-5 lg:hidden">
          <Logo size={32} withWordmark />
        </div>

        <div className="w-full max-w-sm">
          <p className="eyebrow">Sign in</p>
          <h1 className="mt-2 font-heading text-[28px] font-semibold leading-tight tracking-tight text-ink">
            Take the desk
          </h1>
          <p className="mt-2 text-[13.5px] leading-relaxed text-ink-muted">
            Authenticate with your work account to continue. Access requires an
            admin role.
          </p>

          <span aria-hidden className="mt-5 block h-px w-10 bg-brand" />

          {error ? (
            <div className="mt-6 flex items-start gap-2.5 rounded-md border border-danger/30 bg-danger/[0.04] px-3 py-2.5 text-[13px] text-danger">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{error}</p>
            </div>
          ) : null}

          <div className="mt-7 space-y-3">
            <div className="flex justify-center">
              {GOOGLE_CLIENT_ID ? (
                <div ref={googleHost} className="min-h-[44px]" aria-busy={busy === 'google'} />
              ) : (
                <p className="text-sm text-ink-subtle">Google sign-in not configured.</p>
              )}
            </div>

            <div className="flex items-center gap-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-ink-subtle">
              <div className="h-px flex-1 bg-line" />
              email
              <div className="h-px flex-1 bg-line" />
            </div>

            {emailNotice ? (
              <p className="rounded-md border border-brand/30 bg-brand/[0.05] px-3 py-2.5 text-[13px] text-ink">
                {emailNotice}
              </p>
            ) : null}

            {emailMode === 'login' ? (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@von.gov.ng"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                <Button
                  className="w-full justify-center"
                  onClick={() => void handleEmailLogin()}
                  disabled={busy !== null || !email.trim() || !password}
                >
                  {busy === 'email' ? 'Signing in…' : 'Sign in with email'}
                </Button>
                <div className="flex justify-end text-[13px]">
                  <button
                    type="button"
                    className="text-brand hover:underline"
                    onClick={() => switchMode('forgot')}
                  >
                    Forgot password?
                  </button>
                </div>
              </div>
            ) : null}

            {emailMode === 'verify' ? (
              <div className="space-y-3">
                <p className="text-[13px] leading-relaxed text-ink-muted">
                  Enter the 6-digit code we emailed to <strong>{email}</strong>. It expires in 15
                  minutes.
                </p>
                <div className="space-y-1.5">
                  <Label htmlFor="code">Code</Label>
                  <Input
                    id="code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                  />
                </div>
                <Button
                  className="w-full justify-center"
                  onClick={() => void handleEmailVerify()}
                  disabled={busy !== null || code.trim().length !== 6}
                >
                  {busy === 'email' ? 'Verifying…' : 'Verify'}
                </Button>
                <div className="flex items-center justify-between text-[13px]">
                  <button
                    type="button"
                    className="text-brand hover:underline disabled:opacity-50"
                    disabled={busy !== null}
                    onClick={() => {
                      setBusy('email');
                      setError(null);
                      void emailResendCode(email.trim())
                        .then(() => setEmailNotice('Code sent — check your inbox.'))
                        .catch((err: unknown) => setError(formatError(err)))
                        .finally(() => setBusy(null));
                    }}
                  >
                    Resend code
                  </button>
                  <button
                    type="button"
                    className="text-brand hover:underline"
                    onClick={() => switchMode('login')}
                  >
                    Back to sign in
                  </button>
                </div>
              </div>
            ) : null}

            {emailMode === 'forgot' ? (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="fp-email">Email</Label>
                  <Input
                    id="fp-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@von.gov.ng"
                  />
                </div>
                <Button
                  className="w-full justify-center"
                  onClick={() => void handleEmailForgot()}
                  disabled={busy !== null || !email.trim()}
                >
                  {busy === 'email' ? 'Sending…' : 'Send reset code'}
                </Button>
                <button
                  type="button"
                  className="text-[13px] text-brand hover:underline"
                  onClick={() => switchMode('login')}
                >
                  Back to sign in
                </button>
              </div>
            ) : null}

            {emailMode === 'reset' ? (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="rs-code">Code</Label>
                  <Input
                    id="rs-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rs-password">New password (8+ characters)</Label>
                  <Input
                    id="rs-password"
                    type="password"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </div>
                <Button
                  className="w-full justify-center"
                  onClick={() => void handleEmailReset()}
                  disabled={busy !== null || code.trim().length !== 6 || newPassword.length < 8}
                >
                  {busy === 'email' ? 'Resetting…' : 'Reset password'}
                </Button>
                <button
                  type="button"
                  className="text-[13px] text-brand hover:underline"
                  onClick={() => switchMode('login')}
                >
                  Back to sign in
                </button>
              </div>
            ) : null}
          </div>

          <p className="mt-8 text-[12.5px] text-ink-subtle">
            Need access to the admin panel?{' '}
            <Link to="/" className="text-brand hover:underline">
              Talk to your editor
            </Link>
            .
          </p>

          <p className="mt-12 font-mono text-[10px] uppercase tracking-[0.16em] text-ink-subtle">
            {design.organisation.shortName} · {design.organisation.abbreviation} · Admin v0.1
          </p>
        </div>
      </main>
    </div>
  );
}

function formatError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401) return 'Your sign-in was rejected by the server. Try again.';
    if (err.status === 503) return 'The server has not been configured for this provider yet.';
    return err.message;
  }
  return (err as Error).message ?? 'sign-in failed';
}
